/**
 * Camunda 7.18 External Task REST API Service
 * https://docs.camunda.org/manual/7.18/reference/rest/external-task/
 */

import { CamundaApiClient, getCamundaApiClient } from '../CamundaApiClient';
import type {
  ExternalTask,
  ExternalTaskQuery,
  CountResult,
  FetchExternalTasksDto,
  LockedExternalTask,
  CompleteExternalTaskDto,
  ExternalTaskFailureDto,
  ExternalTaskBpmnErrorDto,
  ExtendLockDto,
  SetRetriesDto,
  SetRetriesAsyncDto,
  SetPriorityDto,
  Batch,
} from '../types';

export class ExternalTaskService {
  private client: CamundaApiClient;
  private basePath = '/engine-rest/external-task';

  constructor(client?: CamundaApiClient) {
    this.client = client || getCamundaApiClient();
  }

  // ============================================================================
  // Get List
  // ============================================================================

  /**
   * Get a list of external tasks
   */
  async getList(query?: ExternalTaskQuery): Promise<ExternalTask[]> {
    const params = this.buildQueryParams(query);
    return this.client.get<ExternalTask[]>(`${this.basePath}${params}`);
  }

  /**
   * Get external tasks count
   */
  async getCount(query?: ExternalTaskQuery): Promise<CountResult> {
    const params = this.buildQueryParams(query);
    return this.client.get<CountResult>(`${this.basePath}/count${params}`);
  }

  /**
   * Query external tasks (POST)
   */
  async queryList(query: ExternalTaskQuery): Promise<ExternalTask[]> {
    const { firstResult, maxResults, sortBy, sortOrder, ...body } = query;
    const params = this.buildPaginationParams({ firstResult, maxResults, sortBy, sortOrder });
    return this.client.post<ExternalTask[]>(`${this.basePath}${params}`, body);
  }

  /**
   * Query external tasks count (POST)
   */
  async queryCount(query: ExternalTaskQuery): Promise<CountResult> {
    return this.client.post<CountResult>(`${this.basePath}/count`, query);
  }

  // ============================================================================
  // Get Single
  // ============================================================================

  /**
   * Get external task by ID
   */
  async getById(id: string): Promise<ExternalTask> {
    return this.client.get<ExternalTask>(`${this.basePath}/${id}`);
  }

  /**
   * Get error details for an external task
   */
  async getErrorDetails(id: string): Promise<string> {
    return this.client.getText(`${this.basePath}/${id}/errorDetails`);
  }

  // ============================================================================
  // Fetch and Lock
  // ============================================================================

  /**
   * Fetch and lock external tasks
   */
  async fetchAndLock(dto: FetchExternalTasksDto): Promise<LockedExternalTask[]> {
    return this.client.post<LockedExternalTask[]>(`${this.basePath}/fetchAndLock`, dto);
  }

  // ============================================================================
  // Complete
  // ============================================================================

  /**
   * Complete an external task
   */
  async complete(id: string, dto: CompleteExternalTaskDto): Promise<void> {
    await this.client.post<void>(`${this.basePath}/${id}/complete`, dto);
  }

  // ============================================================================
  // Handle Failure
  // ============================================================================

  /**
   * Handle external task failure
   */
  async handleFailure(id: string, dto: ExternalTaskFailureDto): Promise<void> {
    await this.client.post<void>(`${this.basePath}/${id}/failure`, dto);
  }

  // ============================================================================
  // Handle BPMN Error
  // ============================================================================

  /**
   * Handle BPMN error for external task
   */
  async handleBpmnError(id: string, dto: ExternalTaskBpmnErrorDto): Promise<void> {
    await this.client.post<void>(`${this.basePath}/${id}/bpmnError`, dto);
  }

  // ============================================================================
  // Extend Lock
  // ============================================================================

  /**
   * Extend lock on an external task
   */
  async extendLock(id: string, dto: ExtendLockDto): Promise<void> {
    await this.client.post<void>(`${this.basePath}/${id}/extendLock`, dto);
  }

  // ============================================================================
  // Unlock
  // ============================================================================

  /**
   * Unlock an external task
   */
  async unlock(id: string): Promise<void> {
    await this.client.post<void>(`${this.basePath}/${id}/unlock`, {});
  }

  // ============================================================================
  // Retries
  // ============================================================================

  /**
   * Set retries for an external task
   */
  async setRetries(id: string, dto: SetRetriesDto): Promise<void> {
    await this.client.put<void>(`${this.basePath}/${id}/retries`, dto);
  }

  /**
   * Set retries for multiple external tasks
   */
  async setRetriesSync(dto: {
    externalTaskIds?: string[];
    processInstanceIds?: string[];
    externalTaskQuery?: ExternalTaskQuery;
    processInstanceQuery?: any;
    historicProcessInstanceQuery?: any;
    retries: number;
  }): Promise<void> {
    await this.client.put<void>(`${this.basePath}/retries`, dto);
  }

  /**
   * Set retries async
   */
  async setRetriesAsync(dto: SetRetriesAsyncDto): Promise<Batch> {
    return this.client.post<Batch>(`${this.basePath}/retries-async`, dto);
  }

  // ============================================================================
  // Priority
  // ============================================================================

  /**
   * Set priority for an external task
   */
  async setPriority(id: string, dto: SetPriorityDto): Promise<void> {
    await this.client.put<void>(`${this.basePath}/${id}/priority`, dto);
  }

  // ============================================================================
  // Topic Names
  // ============================================================================

  /**
   * Get external task topic names
   */
  async getTopicNames(options?: {
    withLockedTasks?: boolean;
    withUnlockedTasks?: boolean;
    withRetriesLeft?: boolean;
  }): Promise<string[]> {
    const params = new URLSearchParams();
    if (options?.withLockedTasks) params.append('withLockedTasks', 'true');
    if (options?.withUnlockedTasks) params.append('withUnlockedTasks', 'true');
    if (options?.withRetriesLeft) params.append('withRetriesLeft', 'true');
    const queryString = params.toString();
    return this.client.get<string[]>(`${this.basePath}/topic-names${queryString ? `?${queryString}` : ''}`);
  }

  // ============================================================================
  // Helper Methods
  // ============================================================================

  private buildQueryParams(query?: ExternalTaskQuery): string {
    if (!query) return '';
    const params = new URLSearchParams();
    Object.entries(query).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        if (Array.isArray(value)) {
          params.append(key, value.join(','));
        } else if (typeof value === 'object') {
          params.append(key, JSON.stringify(value));
        } else {
          params.append(key, String(value));
        }
      }
    });
    const queryString = params.toString();
    return queryString ? `?${queryString}` : '';
  }

  private buildPaginationParams(params: {
    firstResult?: number;
    maxResults?: number;
    sortBy?: string;
    sortOrder?: string;
  }): string {
    const urlParams = new URLSearchParams();
    if (params.firstResult !== undefined) urlParams.append('firstResult', String(params.firstResult));
    if (params.maxResults !== undefined) urlParams.append('maxResults', String(params.maxResults));
    if (params.sortBy) urlParams.append('sortBy', params.sortBy);
    if (params.sortOrder) urlParams.append('sortOrder', params.sortOrder);
    const queryString = urlParams.toString();
    return queryString ? `?${queryString}` : '';
  }
}

// Singleton instance
let externalTaskServiceInstance: ExternalTaskService | null = null;

export function getExternalTaskService(): ExternalTaskService {
  if (!externalTaskServiceInstance) {
    externalTaskServiceInstance = new ExternalTaskService();
  }
  return externalTaskServiceInstance;
}

export default ExternalTaskService;
